<template>
  <div class="page page-performance">
    <LayoutContainer>
      <div class="page-header">
        <h1 class="title">
          <i class="fas fa-chart-line icon"></i> Performance
        </h1>
        <div class="filter">
          <FilterDate
            :defaultFilter="'forever'"
            :disabledRangeDateKey="disabledDate" />
        </div>
      </div>
      <div class="page-content">
        <div class="row">
          <div class="col-12">
            <div class="section-container">
              <PreLoaderSection
                :options="{ 'height': state.buffer.height }"
                v-show="state.buffer.isPreLoaderShowing"
              />
              <SectionBuffer
                :bufferData="bufferSection"
                v-show="!state.buffer.isPreLoaderShowing"
                :isLoading="state.buffer.isLoading"
                :isError="state.buffer.isError"
                :error="state.buffer.error"
              />
            </div>
          </div>
          <div class="col-12 col-md-12 col-lg-6 col-xl-8" v-if="hasAdsSection">
            <div class="section-container">
              <PreLoaderSection
                :options="{ 'height': state.ads.height }"
                v-show="state.ads.isPreLoaderShowing"
              />
              <SectionAdsBlock
                :adsData="adsSection"
                v-show="!state.ads.isPreLoaderShowing"
                :isLoading="state.ads.isLoading"
                :isError="state.ads.isError"
                :error="state.ads.error"
              />
            </div>
          </div>
          <div class="col-12 col-md-12 col-lg-6 col-xl-4" v-if="hasAdsSection">
            <div class="section-container">
              <PreLoaderSection
                :options="{ 'height' : state.ads.height }"
                v-show="state.ads.isPreLoaderShowing"
              />
              <SectionAdsPerformance
                :adsData="adsSection"
                v-show="!state.ads.isPreLoaderShowing"
                :isLoading="state.ads.isLoading"
                :isError="state.ads.isError"
                :error="state.ads.error"
              />
            </div>
          </div>
          <div class="col-12 col-md-12 col-lg-6">
            <div class="section-container">
              <PreLoaderSection
                :options="{ 'height' : state.resolution.height }"
                v-show="state.resolution.isPreLoaderShowing"
              />
              <SectionResolution
                :resolutionData="resolutionSection"
                :isLoading="state.resolution.isLoading"
                :isError="state.resolution.isError"
                :error="state.resolution.error"
                v-show="!state.resolution.isPreLoaderShowing"
              />
            </div>
          </div>
          <div class="col-12 col-md-12 col-lg-6">
            <div class="section-container">
              <PreLoaderSection
                :options="{ 'height' : state.browser.height }"
                v-show="state.browser.isPreLoaderShowing"
              />
              <SectionBrowser
                :browserData="userAgentSection"
                :isLoading="state.browser.isLoading"
                :isError="state.browser.isError"
                :error="state.browser.error"
                v-show="!state.browser.isPreLoaderShowing"
              />
            </div>
          </div>
          <div class="col-12 col-md-12 col-lg-6">
            <div class="section-container">
              <PreLoaderSection
                :options="{ 'height' : state.os.height }"
                v-show="state.os.isPreLoaderShowing"
              />
              <SectionOS
                :osData="osSection"
                :isLoading="state.os.isLoading"
                :isError="state.os.isError"
                :error="state.os.error"
                v-show="!state.os.isPreLoaderShowing"
              />
            </div>
          </div>
          <div class="col-12 col-md-12 col-lg-6">
            <div class="section-container">
              <PreLoaderSection
                :options="{ 'height' : state.isp.height }"
                v-show="state.isp.isPreLoaderShowing"
              />
              <SectionISP
                :ispData="ispSection"
                :isLoading="state.isp.isLoading"
                :isError="state.isp.isError"
                :error="state.isp.error"
                v-show="!state.isp.isPreLoaderShowing"
              />
            </div>
          </div>
        </div>
      </div>
    </LayoutContainer>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import {
  getPerformanceBuffer,
  getPlaybackResolutionPerformance,
  getUserAgent,
  getOSFamily,
  getISPPerformance,
  getAdsPerformance,
} from './api';
import generateLink from '@/utils/generateLink';
import LayoutContainer from '@/layout/LayoutContainer.vue';
import FilterDate from '@/components/FilterDate.vue';
import SectionBuffer from '@/components/SectionBuffer.vue';
import SectionResolution from '@/components/SectionResolution.vue';
import SectionBrowser from '@/components/SectionBrowser.vue';
import SectionOS from '@/components/SectionOS.vue';
import SectionISP from '@/components/SectionISP.vue';
import SectionAdsBlock from '@/components/SectionAdsBlock.vue';
import SectionAdsPerformance from '@/components/SectionAdsPerformance.vue';
import PreLoaderSection from '@/components/PreLoaderSection.vue';

export default {
  mixins: [generateLink],
  components: {
    FilterDate,
    SectionBuffer,
    SectionResolution,
    SectionBrowser,
    SectionOS,
    SectionISP,
    PreLoaderSection,
    LayoutContainer,
    SectionAdsBlock,
    SectionAdsPerformance,
  },
  data() {
    return {
      disabledDate: ['forever', 'today', 'last7days', 'last30days', 'custom'],
      bufferSection: null,
      resolutionSection: null,
      userAgentSection: null,
      osSection: null,
      ispSection: null,
      adsSection: null,
      state: {
        buffer: {
          height: '400',
          isPreLoaderShowing: true,
          isLoading: false,
          isError: false,
          error: null,
        },
        resolution: {
          height: '350',
          isPreLoaderShowing: true,
          isLoading: false,
          isError: false,
          error: null,
        },
        browser: {
          height: '350',
          isPreLoaderShowing: true,
          isLoading: false,
          isError: false,
          error: null,
        },
        os: {
          height: '350',
          isPreLoaderShowing: true,
          isLoading: false,
          isError: false,
          error: null,
        },
        isp: {
          height: '350',
          isPreLoaderShowing: true,
          isLoading: false,
          isError: false,
          error: null,
        },
        ads: {
          height: '400',
          isPreLoaderShowing: true,
          isLoading: false,
          isError: false,
          error: null,
        },
      },
    };
  },
  computed: {
    ...mapGetters(['currentProject', 'getProjectById']),
    projectId() {
      return this.$route.params.projectId;
    },
    hasAdsSection() {
      return _.get(this.adsSection, 'has_ads', false);
    },
  },
  watch: {
    '$route.query': {
      handler() {
        const filter = this.generateQuery(this.$route.query);
        this.disPatchActions(filter);
      },
    },
  },
  async created() {
    let filter = this.generateQuery(this.$route.query);
    if (_.isEmpty(filter)) {
      this.$router.push({
        name: 'performance',
        query: { range: 'last24hours' },
      });
      filter = { range: 'last24hours' };
    } else {
      filter = this.generateQuery(this.$route.query);
    }
    if (this.currentProject.projectId !== this.projectId) {
      await this.$store.dispatch(
        'setCurrentProject',
        this.getProjectById(this.projectId),
      );
    }
    this.disPatchActions(filter);
  },
  methods: {
    disPatchActions(filter) {
      this.getBufferSection(filter);
      this.getResolutionBufferSection(filter);
      this.getUserAgentSection(filter);
      this.getOSSection(filter);
      this.getISPSection(filter);
      this.getAdsSection(filter);
    },
    async getBufferSection(filter) {
      this.state.buffer.isLoading = true;
      this.bufferSection = null;
      try {
        const response = await getPerformanceBuffer(filter);
        this.bufferSection = response.data;
        this.state.buffer.isPreLoaderShowing = false;
        this.state.buffer.isLoading = false;
        this.state.buffer.isError = false;
      } catch (error) {
        this.state.buffer.isPreLoaderShowing = false;
        this.state.buffer.isError = true;
        this.state.buffer.isLoading = false;
        this.state.buffer.error = error.response;
      }
    },
    async getResolutionBufferSection(filter) {
      this.state.resolution.isLoading = true;
      this.resolutionSection = null;
      try {
        const response = await getPlaybackResolutionPerformance(filter);
        this.resolutionSection = response.data.data;
        this.state.resolution.isPreLoaderShowing = false;
        this.state.resolution.isLoading = false;
        this.state.resolution.isError = false;
      } catch (error) {
        this.state.resolution.isPreLoaderShowing = false;
        this.state.resolution.isError = true;
        this.state.resolution.isLoading = false;
        this.state.resolution.error = error.response;
      }
    },
    async getUserAgentSection(filter) {
      this.state.browser.isLoading = true;
      const optionalQuery = { ...filter, top: 7 };
      this.userAgentSection = null;
      try {
        const response = await getUserAgent(optionalQuery);
        this.userAgentSection = response.data.data;
        this.state.browser.isPreLoaderShowing = false;
        this.state.browser.isLoading = false;
        this.state.browser.isError = false;
      } catch (error) {
        this.state.browser.isPreLoaderShowing = false;
        this.state.browser.isError = true;
        this.state.browser.isLoading = false;
        this.state.browser.error = error.response;
      }
    },
    async getOSSection(filter) {
      this.state.os.isLoading = true;
      const optionalQuery = { ...filter, top: 7 };
      this.osSection = null;
      try {
        const response = await getOSFamily(optionalQuery);
        this.osSection = response.data.data;
        this.state.os.isPreLoaderShowing = false;
        this.state.os.isLoading = false;
        this.state.os.isError = false;
      } catch (error) {
        this.state.os.isPreLoaderShowing = false;
        this.state.os.isError = true;
        this.state.os.isLoading = false;
        this.state.os.error = error.response;
      }
    },
    async getISPSection(filter) {
      this.state.isp.isLoading = true;
      const optionalQuery = { ...filter, top: 10 };
      this.ispSection = null;
      try {
        const response = await getISPPerformance(optionalQuery);
        this.ispSection = response.data.data;
        this.state.isp.isPreLoaderShowing = false;
        this.state.isp.isLoading = false;
        this.state.isp.isError = false;
      } catch (error) {
        this.state.isp.isPreLoaderShowing = false;
        this.state.isp.isError = true;
        this.state.isp.isLoading = false;
        this.state.isp.error = error.response;
      }
    },
    async getAdsSection(filter) {
      this.state.ads.isLoading = true;
      this.adsSection = null;
      try {
        const response = await getAdsPerformance(filter);
        this.adsSection = response.data.data;
        this.state.ads.isPreLoaderShowing = false;
        this.state.ads.isLoading = false;
        this.state.ads.isError = false;
      } catch (error) {
        this.state.ads.isPreLoaderShowing = false;
        this.state.ads.isError = true;
        this.state.ads.isLoading = false;
        this.state.ads.error = error.response;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
@import '@/assets/scss/themes/components/page.scss';

.section-container {
  margin-bottom: $spacer;
}
</style>
